import React, { useState, useRef, useEffect } from 'react';
import { NavLink, Link } from 'react-router-dom';
import { useIsAuthenticated, useMsal } from '@azure/msal-react';
import { loginRequest } from '@/authConfig';
import { useIsAdmin } from '@/hooks/useIsAdmin';
import logo from '@/assets/Logo.png';

interface NavItem {
  path: string;
  label: string;
}

const navItems: NavItem[] = [
  { path: '/', label: 'Home' },
  { path: '/documents', label: 'Documents' },
  { path: '/resources', label: 'Resources' },
  { path: '/marketing', label: 'Marketing' },
  { path: '/designs', label: 'Designs' },
  { path: '/engineering', label: 'Engineering & Technology' },
  { path: '/b2b', label: 'B2B' },
  { path: '/socials', label: 'Socials' },
];

interface MenuLink { path: string; label: string; /** Lives in the Admin area, so only admins can open it. */ admin?: boolean; }
interface MenuGroup { heading?: string; links: MenuLink[]; }

const marketplaceLinks: MenuLink[] = [
  { path: '/markets', label: 'Markets' },
  { path: '/surge-pricing', label: 'Surge Pricing' },
  { path: '/surge-pricing/audit', label: 'Audit Log' },
];

// Every analytics surface in one menu. The pages keep their existing URLs.
const analyticsGroups: MenuGroup[] = [
  {
    heading: 'Growth',
    links: [
      { path: '/analytics', label: 'Founder Scorecard' },
      { path: '/analytics/referrals', label: 'Referral Analytics' },
      { path: '/businesses?tab=referral-codes', label: 'Referral Codes', admin: true },
      { path: '/socials/attribution', label: 'Growth Attribution' },
      { path: '/socials/stat-sheets', label: 'Stat Sheets' },
    ],
  },
  {
    heading: 'Engagement',
    links: [
      { path: '/analytics/friends', label: 'Friend Analytics' },
      { path: '/businesses?tab=notification-opens', label: 'Notification Opens', admin: true },
      { path: '/b2b/user-insights', label: 'User Insights' },
      { path: '/businesses?tab=monitoring', label: 'Monitoring', admin: true },
    ],
  },
  {
    heading: 'Economy',
    links: [{ path: '/businesses?tab=gamification&sub=economy', label: 'Economy Health', admin: true }],
  },
  {
    heading: 'B2B',
    links: [
      { path: '/b2b/notification-metrics', label: 'Notification Performance' },
      { path: '/b2b/business-analytics', label: 'Business Insights' },
    ],
  },
];

const visibleGroups = (groups: MenuGroup[], isAdmin: boolean): MenuGroup[] =>
  groups
    .map((g) => ({ ...g, links: g.links.filter((l) => isAdmin || !l.admin) }))
    .filter((g) => g.links.length > 0);

function NavDropdown({ label, groups }: { label: string; groups: MenuGroup[] }): React.ReactElement {
  const [open, setOpen] = useState(false);
  const btnRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent) => {
      if (btnRef.current && !btnRef.current.parentElement?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open]);

  return (
    <div className="relative">
      <button
        ref={btnRef}
        onClick={() => setOpen((v) => !v)}
        className="relative px-3.5 py-2 text-[14px] font-medium rounded-lg transition-all duration-150 whitespace-nowrap text-gray-300 hover:text-white hover:bg-gray-700 flex items-center gap-1"
      >
        {label}
        <svg className="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
          <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {open && (
        <div className="absolute top-full mt-1 left-0 z-50 w-56 bg-gray-900/95 backdrop-blur-sm border border-gray-700 rounded-xl shadow-xl py-1.5">
          {groups.map((g) => (
            <div key={g.heading ?? g.links[0].path}>
              {g.heading && (
                <p className="px-4 pt-2 pb-1 text-[10px] font-semibold uppercase tracking-widest text-gray-500">
                  {g.heading}
                </p>
              )}
              {g.links.map((l) =>
                // Entries that differ only by query share a pathname, which
                // NavLink's active state cannot tell apart, so those are plain links.
                l.path.includes('?') ? (
                  <Link
                    key={l.path}
                    to={l.path}
                    onClick={() => setOpen(false)}
                    className="block px-4 py-2 text-sm rounded-lg mx-1 transition-all text-gray-300 hover:text-white hover:bg-gray-800 no-underline"
                  >
                    {l.label}
                  </Link>
                ) : (
                  <NavLink
                    key={l.path}
                    to={l.path}
                    end
                    onClick={() => setOpen(false)}
                    className={({ isActive }) =>
                      `block px-4 py-2 text-sm rounded-lg mx-1 transition-all ${
                        isActive ? 'text-violet-300 bg-gray-800' : 'text-gray-300 hover:text-white hover:bg-gray-800'
                      }`
                    }
                  >
                    {l.label}
                  </NavLink>
                ),
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Header(): React.ReactElement {
  const [mobileMenuOpen, setMobileMenuOpen] = useState<boolean>(false);
  const [showAdminCard, setShowAdminCard] = useState(false);
  const isAuthenticated = useIsAuthenticated();
  const { instance, accounts } = useMsal();
  const isAdmin = useIsAdmin();
  const avatarRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!showAdminCard) return;
    const handle = (e: MouseEvent) => {
      if (avatarRef.current && !avatarRef.current.contains(e.target as Node)) {
        setShowAdminCard(false);
      }
    };
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, [showAdminCard]);

  const handleLogin = () => {
    instance.loginPopup(loginRequest).catch((error) => {
      console.error('Login failed:', error);
    });
  };

  const handleLogout = () => {
    instance.logoutPopup().catch((error) => {
      console.error('Logout failed:', error);
    });
  };

  const userName = accounts[0]?.name || accounts[0]?.username || 'User';
  const userInitials = userName.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);

  return (
    <header className="sticky top-0 z-50 bg-gray-950/95 backdrop-blur-md border-b border-gray-800 h-[72px]">
      <div className="max-w-[1400px] mx-auto px-6 h-full flex items-center justify-between gap-8">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-0.5 no-underline flex-shrink-0">
          <div className="w-10 h-10">
            <img src={logo} alt="SocialSpark" className="w-full h-full object-cover rounded-full" />
          </div>
          <span className="font-display text-xl font-bold">
            <span className="text-white">Social</span>
            <span className="bg-gradient-to-r from-violet-400 to-pink-400 bg-clip-text text-transparent">Spark</span>
          </span>
        </Link>

        {/* Desktop Navigation */}
        <nav className="hidden lg:flex items-center gap-1 flex-1 justify-center">
          {navItems.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              className={({ isActive }) =>
                `relative px-3.5 py-2 text-[14px] font-medium rounded-lg transition-all duration-150 whitespace-nowrap ${
                  isActive
                    ? 'text-violet-300 bg-gray-800'
                    : 'text-gray-300 hover:text-white hover:bg-gray-700'
                }`
              }
            >
              {item.label}
            </NavLink>
          ))}
          <NavDropdown label="Analytics" groups={visibleGroups(analyticsGroups, isAdmin)} />
          <NavDropdown label="Marketplace" groups={[{ links: marketplaceLinks }]} />
          {isAdmin && (
            <NavLink
              to="/businesses"
              className={({ isActive }) =>
                `relative px-3.5 py-2 text-[14px] font-medium rounded-lg transition-all duration-150 whitespace-nowrap flex items-center gap-1.5 ${
                  isActive
                    ? 'text-violet-300 bg-gray-800'
                    : 'text-violet-400 hover:text-violet-200 hover:bg-gray-700'
                }`
              }
            >
              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z" />
              </svg>
              Admin
            </NavLink>
          )}
        </nav>

        {/* Right side actions */}
        <div className="hidden md:flex items-center gap-3 flex-shrink-0">
          
          {isAuthenticated ? (
            <div className="flex items-center gap-2">
              <div className="relative">
                <button
                  ref={avatarRef}
                  onClick={() => isAdmin && setShowAdminCard(v => !v)}
                  className={`w-10 h-10 bg-gradient-to-r from-violet-700 to-pink-700 rounded-full flex items-center justify-center text-white font-semibold text-sm transition-all ${isAdmin ? 'cursor-pointer hover:ring-2 hover:ring-violet-500 hover:ring-offset-2 hover:ring-offset-gray-950' : 'cursor-default'}`}
                  title={userName}
                >
                  <span>{userInitials}</span>
                </button>
                {showAdminCard && (
                  <div className="absolute top-14 left-1/2 -translate-x-1/2 z-50 w-44 bg-gray-900/95 backdrop-blur-sm border border-violet-800/40 rounded-xl shadow-xl p-3 flex flex-col items-center gap-1.5 text-center">
                    <p className="text-xs font-semibold text-white truncate max-w-full px-1">{userName}</p>
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-violet-950/80 border border-violet-700/50 rounded-full text-[10px] font-medium text-violet-300 tracking-wide">
                      <svg className="w-2.5 h-2.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z" />
                      </svg>
                      Admin
                    </span>
                  </div>
                )}
              </div>
              <button 
                onClick={handleLogout}
                className="px-3 py-1.5 text-sm font-medium text-gray-200 bg-gray-800 hover:text-white hover:bg-gray-700 rounded-lg transition-all"
              >
                Sign Out
              </button>
            </div>
          ) : (
            <button 
              onClick={handleLogin}
              className="px-4 py-2 text-sm font-medium text-white bg-gradient-to-r from-violet-700 to-pink-700 rounded-lg hover:opacity-90 transition-all"
            >
              Sign In
            </button>
          )}
        </div>

        {/* Mobile menu button */}
        <button 
          className="flex lg:hidden items-center justify-center w-11 h-11 bg-gray-800 border border-gray-700 rounded-lg cursor-pointer p-0"
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          aria-label="Toggle menu"
        >
          <span className="flex flex-col gap-[5px] w-5">
            <span className={`block h-0.5 w-full bg-gray-300 rounded transition-all duration-150 ${mobileMenuOpen ? 'translate-y-[7px] rotate-45' : ''}`}></span>
            <span className={`block h-0.5 w-full bg-gray-300 rounded transition-all duration-150 ${mobileMenuOpen ? 'opacity-0' : ''}`}></span>
            <span className={`block h-0.5 w-full bg-gray-300 rounded transition-all duration-150 ${mobileMenuOpen ? '-translate-y-[7px] -rotate-45' : ''}`}></span>
          </span>
        </button>
      </div>

      {/* Mobile Navigation */}
      <nav className={`${mobileMenuOpen ? 'flex' : 'hidden'} absolute top-[72px] left-0 right-0 bg-gray-900 border-b border-gray-800 p-4 flex-col gap-1 shadow-lg lg:hidden`}>
        {navItems.map((item) => (
          <NavLink
            key={item.path}
            to={item.path}
            className={({ isActive }) =>
              `px-4 py-3 text-[15px] font-medium rounded-lg transition-all duration-150 ${
                isActive
                  ? 'text-violet-300 bg-gray-800'
                  : 'text-gray-300 hover:text-white hover:bg-gray-700'
              }`
            }
            onClick={() => setMobileMenuOpen(false)}
          >
            {item.label}
          </NavLink>
        ))}
        {visibleGroups(analyticsGroups, isAdmin).map((g) => (
          <React.Fragment key={g.heading}>
            <p className="px-4 pt-3 pb-1 text-[11px] font-semibold uppercase tracking-widest text-gray-500">
              Analytics · {g.heading}
            </p>
            {g.links.map((l) => (
              <Link
                key={l.path}
                to={l.path}
                onClick={() => setMobileMenuOpen(false)}
                className="px-4 py-3 text-[15px] font-medium rounded-lg transition-all duration-150 text-gray-300 hover:text-white hover:bg-gray-700 no-underline"
              >
                {l.label}
              </Link>
            ))}
          </React.Fragment>
        ))}
        {marketplaceLinks.map((l) => (
          <NavLink
            key={l.path}
            to={l.path}
            onClick={() => setMobileMenuOpen(false)}
            className={({ isActive }) =>
              `px-4 py-3 text-[15px] font-medium rounded-lg transition-all duration-150 ${
                isActive ? 'text-violet-300 bg-gray-800' : 'text-gray-300 hover:text-white hover:bg-gray-700'
              }`
            }
          >
            {l.label}
          </NavLink>
        ))}
        {isAdmin && (
          <NavLink
            to="/businesses"
            className={({ isActive }) =>
              `px-4 py-3 text-[15px] font-medium rounded-lg transition-all duration-150 flex items-center gap-2 ${
                isActive
                  ? 'text-violet-300 bg-gray-800'
                  : 'text-violet-400 hover:text-violet-200 hover:bg-gray-700'
              }`
            }
            onClick={() => setMobileMenuOpen(false)}
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z" />
            </svg>
            Admin
          </NavLink>
        )}

        {/* Auth section — mobile-only mirror of the desktop avatar/sign-out
            block. Without this there is no way to sign out on a phone (the
            desktop block uses `hidden md:flex`). */}
        <div className="mt-3 pt-3 border-t border-gray-800 flex flex-col gap-1">
          {isAuthenticated ? (
            <>
              <div className="flex items-center gap-3 px-4 py-3">
                <div className="w-9 h-9 bg-gradient-to-r from-violet-700 to-pink-700 rounded-full flex items-center justify-center text-white font-semibold text-sm flex-shrink-0">
                  <span>{userInitials}</span>
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-white truncate">{userName}</p>
                  {isAdmin && (
                    <span className="inline-flex items-center gap-1 mt-0.5 px-2 py-0.5 bg-violet-950/80 border border-violet-700/50 rounded-full text-[10px] font-medium text-violet-300 tracking-wide">
                      <svg className="w-2.5 h-2.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z" />
                      </svg>
                      Admin
                    </span>
                  )}
                </div>
              </div>
              <button
                type="button"
                onClick={() => {
                  setMobileMenuOpen(false);
                  handleLogout();
                }}
                className="px-4 py-3 text-[15px] font-medium text-gray-200 bg-gray-800 hover:text-white hover:bg-gray-700 rounded-lg transition-all text-left"
              >
                Sign Out
              </button>
            </>
          ) : (
            <button
              type="button"
              onClick={() => {
                setMobileMenuOpen(false);
                handleLogin();
              }}
              className="px-4 py-3 text-[15px] font-medium text-white bg-gradient-to-r from-violet-700 to-pink-700 rounded-lg hover:opacity-90 transition-all text-left"
            >
              Sign In
            </button>
          )}
        </div>
      </nav>
    </header>
  );
}

export default Header;
